/**
 * lx-voice-view —— 语音输入浮层
 *
 * 半透明底部上浮卡片（设计文档 3.7）。展示实时识别文字，
 * 让用户看到"我说的话正在被转成字"。
 *
 * M5 起它由「按住说话」驱动：手指在麦克风键上按下时升起，松手时结束。
 * 所以它需要提供两个对外口子：
 *   · onResult          —— 识别出最终文本时回调给输入栏
 *   · onRequestFinish   —— 浮层内点「结束」按钮时，通知输入栏按"松手"处理
 *   · finishFromOutside —— 由输入栏调用来收尾（松手 / 取消）
 *
 * 状态机：starting → listening（实时上屏）→ finishing → done
 * 任何环节出错都转 error，并保持文本输入可用（降级不静默）。
 */

import { LxElement, define } from '@/core/base-element';
import { startRecognition, type SpeechSession } from '@/ai/voice';
import { popOverlay } from '@/core/ui-store';

type Phase = 'starting' | 'listening' | 'finishing' | 'error';

export class LxVoiceView extends LxElement {
  private _phase: Phase = 'starting';
  private _text = '';
  private _error = '';
  private _session: SpeechSession | null = null;
  /** 是否已经收过尾，防止松手 + 点按钮两路并发 */
  private _settled = false;
  /** 识别完成后把文本交给谁 —— 由打开浮层的调用方设置 */
  onResult: ((text: string) => void) | null = null;
  /** 浮层内「结束」按钮被点 —— 由调用方决定怎么收尾 */
  onRequestFinish: (() => void) | null = null;

  protected styles(): string {
    return `
      :host {
        position: absolute;
        inset: 0;
        z-index: var(--z-overlay);
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        background: rgba(0, 0, 0, .35);
        backdrop-filter: blur(4px);
      }

      .card {
        margin: var(--sp-4);
        margin-bottom: calc(var(--sp-8) + env(safe-area-inset-bottom, 0px));
        background: var(--c-surface);
        border: 1px solid var(--c-border);
        border-radius: var(--radius-lg);
        padding: var(--sp-4);
        box-shadow: var(--shadow-lg);
        animation: rise var(--dur-base) var(--ease-standard);
      }
      @keyframes rise {
        from { transform: translateY(12px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }

      .head {
        display: flex; align-items: center; gap: var(--sp-2);
        margin-bottom: var(--sp-3);
        font-size: var(--fs-sm);
        color: var(--c-fg-subtle);
      }

      .pulse {
        width: 10px; height: 10px; border-radius: 50%;
        background: var(--c-primary);
        animation: pulse 1.1s ease-in-out infinite;
      }
      @keyframes pulse {
        0%, 100% { transform: scale(.7); opacity: .5; }
        50% { transform: scale(1.15); opacity: 1; }
      }

      .text {
        min-height: 48px;
        font-size: var(--fs-lg);
        line-height: var(--lh-relaxed, 1.7);
        color: var(--c-fg);
        white-space: pre-wrap;
        word-break: break-word;
      }
      .text.placeholder { color: var(--c-fg-subtle); }

      .hint {
        margin-top: var(--sp-3);
        font-size: var(--fs-xs);
        color: var(--c-fg-subtle);
      }

      .error { color: var(--c-danger, #e5484d); }

      .actions {
        display: flex; gap: var(--sp-2);
        margin-top: var(--sp-4);
      }
      button {
        flex: 1;
        padding: var(--sp-2) var(--sp-3);
        border-radius: var(--radius-md);
        font-size: var(--fs-md);
        border: 1px solid var(--c-border);
        background: var(--c-surface-2);
        color: var(--c-fg);
      }
      button.primary {
        background: var(--c-primary);
        color: var(--c-primary-fg);
        border-color: transparent;
      }
    `;
  }

  protected render(): string {
    const phaseText: Record<Phase, string> = {
      starting: '正在准备麦克风…',
      listening: '正在聆听…',
      finishing: '正在识别…',
      error: '出问题了',
    };

    const showText = this._text || (this._phase === 'error' ? this._error : '说完松手，或点下面的按钮');
    const textClass = this._text || this._phase === 'error' ? '' : 'placeholder';

    return `
      <div class="card">
        <div class="head">
          ${this._phase === 'listening' ? '<span class="pulse"></span>' : ''}
          <span class="${this._phase === 'error' ? 'error' : ''}">${phaseText[this._phase]}</span>
        </div>
        <div class="text ${textClass}" aria-live="polite">${this.esc(showText)}</div>
        <div class="hint">${
          this._phase === 'listening'
            ? '识别结果会实时上屏。松手即结束。'
            : this._phase === 'error'
              ? '文本输入依然可用。'
              : '首次使用会请求麦克风权限。'
        }</div>
        <div class="actions">
          ${
            this._phase === 'error'
              ? `<button class="primary" data-close>知道了</button>`
              : `<button data-cancel>取消</button>
                 ${
                   this._phase === 'listening'
                     ? `<button class="primary" data-finish>结束</button>`
                     : ''
                 }`
          }
        </div>
      </div>
    `;
  }

  protected onMounted(): void {
    this.$('[data-close]')?.addEventListener('click', () => this.close());
    this.$('[data-cancel]')?.addEventListener('click', () => void this.cancel());
    // 「结束」不再由本组件直接收尾 —— 交给输入栏统一处理，
    // 这样「松手」与「点按钮」走的是同一条路径，不会有两套收尾逻辑各自漏边界。
    this.$('[data-finish]')?.addEventListener('click', () => {
      if (this.onRequestFinish) this.onRequestFinish();
      else void this.finish();
    });
  }

  /**
   * 由输入栏调用：松手（abandon=false）或取消（abandon=true）时收尾。
   *
   * 幂等：重复调用只生效一次。因为「手指松手」与「浮层内点结束」
   * 是两条独立的事件源，很可能同时到达。
   */
  async finishFromOutside(abandon: boolean): Promise<void> {
    if (this._settled) return;
    if (abandon) {
      await this.cancel();
    } else {
      await this.finish();
    }
  }

  connectedCallback(): void {
    super.connectedCallback();
    // 渲染完成后启动识别（浮层一出现就开始听）
    queueMicrotask(() => void this.begin());
  }

  protected onUnmounted(): void {
    // 组件被移除时确保麦克风被释放 —— 否则会有个没关的录音在后台
    void this._session?.cancel().catch(() => {});
    this._session = null;
  }

  private async begin(): Promise<void> {
    try {
      const session = await startRecognition();
      this._session = session;

      session.onPartial((text) => {
        this._text = text;
        this.update();
      });

      session.onError(({ message }) => {
        this._phase = 'error';
        this._error = message;
        this.update();
      });

      this._phase = 'listening';
      this.update();
    } catch (err) {
      this._phase = 'error';
      this._error = err instanceof Error ? err.message : String(err);
      this.update();
    }
  }

  private async finish(): Promise<void> {
    if (this._settled) return;
    this._settled = true;
    if (!this._session) {
      this.close();
      return;
    }
    this._phase = 'finishing';
    this.update();
    try {
      const text = await this._session.stop();
      this._session = null;
      if (text.trim()) {
        this.onResult?.(text.trim());
      }
      this.close();
    } catch (err) {
      this._phase = 'error';
      this._error = err instanceof Error ? err.message : String(err);
      this.update();
    }
  }

  private async cancel(): Promise<void> {
    if (this._settled) return;
    this._settled = true;
    await this._session?.cancel().catch(() => {});
    this._session = null;
    this.close();
  }

  private close(): void {
    popOverlay();
  }
}

define('lx-voice-view', LxVoiceView);
